// (c) 2023 ABBYY Development, Inc.

import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from 'components';
import { useBlockNavigation } from 'providers';
import { ApplicationListItems } from './shared/ApplicationList';
import AbstractTemplate from './shared/AbstractTemplate';
import { useAddTemplate } from 'hooks';
import { TEMPLATES_QUERY_KEY } from 'hooks/query-keys';
import { Template } from 'types/template';
import { useQueryClient } from 'react-query';
import { useNotification } from 'libs/notification';

export interface AddTemplateDialogProps {
    open: boolean;
    onRequestClose: () => void;
}

const AddTemplateDialog: FC<AddTemplateDialogProps> = ({
    open,
    onRequestClose
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { mutate: addTemplate, isLoading } = useAddTemplate();
    const { success, error } = useNotification();

    const [blacklist, setBlacklist] = useState<ApplicationListItems>({
        isEnabled: false,
        items: []
    });
    const [whitelist, setWhitelist] = useState<ApplicationListItems>({
        isEnabled: false,
        items: []
    });

    const initialTemplateData: Template = useMemo(() => {
        return {
            blacklist: [],
            blacklistEnabled: false,
            creationTime: undefined,
            fieldRedactionEnabled: false,
            mergeDifferentHosts: false,
            redactionPolicies: [],
            whitelist: [],
            whitelistEnabled: false,
            name: t('general.new_template'),
            recordersCount: 0,
            recordOnStart: false,
            recordExtendedElementData: false,
            readOnly: false,
            transformations: [],
            default: false,
            schedule: {
                enabled: false,
                days: [],
                time: {
                    fromHours: 8,
                    fromMinutes: 0,
                    toHours: 17,
                    toMinutes: 0
                }
            }
        };
    }, [t]);

    const [templateDetails, setTemplateDetails] =
        useState<Template>(initialTemplateData);

    useEffect(() => {
        if (open) {
            setTemplateDetails(initialTemplateData);
        }
    }, [open, initialTemplateData]);

    const [hasError, setHasError] = useState(false);

    const {
        setCallback,
        isNavigationBlocked,
        showBlockModal,
        setIsNavigationBlocked
    } = useBlockNavigation();

    const onAddTemplateRequest = () => {
        const calculatedTemplateDetails: Template = {
            ...templateDetails,
            creationTime: new Date(),
            whitelistEnabled: whitelist.isEnabled,
            blacklistEnabled: blacklist.isEnabled,
            whitelist: whitelist.items,
            blacklist: blacklist.items
        };
        setIsNavigationBlocked(false);

        addTemplate(calculatedTemplateDetails, {
            onSuccess: () => {
                success(
                    t('expressions.add_template_success', {
                        name: calculatedTemplateDetails.name
                    })
                );
            },
            onError: (err) => {
                error(
                    t(
                        `status_codes.${
                            err.response?.status ?? 0
                        }.detailed_message`
                    )
                );
            },
            onSettled: () => {
                queryClient.invalidateQueries(TEMPLATES_QUERY_KEY);
                onRequestClose();
            }
        });
    };

    const close = () => {
        if (isNavigationBlocked) {
            showBlockModal();
            setCallback(() => (ok: boolean) => {
                ok && onRequestClose();
            });
        } else {
            onRequestClose();
        }
    };

    return (
        <Dialog isOpen={open} onClose={close} width={800}>
            <Dialog.Header
                title={t('expressions.add_template')}
                onClose={close}
            />
            <Dialog.Body>
                <AbstractTemplate
                    isReadOnly={false}
                    template={templateDetails}
                    templateSetter={setTemplateDetails}
                    blacklist={blacklist}
                    blacklistSetter={setBlacklist}
                    whitelist={whitelist}
                    whitelistSetter={setWhitelist}
                    isDialog={true}
                    setHasError={setHasError}
                />
            </Dialog.Body>
            <Dialog.Footer>
                <Button type="text" onClick={close}>
                    {t('general.Close')}
                </Button>
                <Button
                    onClick={onAddTemplateRequest}
                    isDisabled={hasError || isLoading}
                    isLoading={isLoading}
                >
                    {t('general.Add')}
                </Button>
            </Dialog.Footer>
        </Dialog>
    );
};

export default AddTemplateDialog;
